import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { useToast } from "@/components/ui/use-toast";
import { motion } from "framer-motion";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

// UI Components
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// Icons
import {
  User,
  Shield,
  AlertTriangle,
  Smartphone,
  Key,
  Lock,
  LogOut,
  Trash2,
  ArrowLeft,
  Save,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  Info,
  Phone,
  Globe,
  AlertCircle,
  Circle,
} from "lucide-react";

// Password update form schema
const passwordFormSchema = z
  .object({
    currentPassword: z.string().min(1, "Current password is required"),
    newPassword: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number")
      .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

// Mobile number form schema
const mobileFormSchema = z.object({
  mobileNumber: z
    .string()
    .min(8, "Mobile number must be at least 8 digits")
    .regex(/^\+?[0-9\s\(\)\-\.]+$/, "Please enter a valid mobile number"),
});

// Delete account form schema
const deleteAccountSchema = z.object({
  confirmText: z.string().min(1, "Confirmation text is required")
    .refine((val) => val === "DELETE", {
      message: 'Please type "DELETE" to confirm',
    }),
});

type PasswordFormValues = z.infer<typeof passwordFormSchema>;
type MobileFormValues = z.infer<typeof mobileFormSchema>;

const Settings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  // const dispatch = useDispatch(); // Uncomment if needed
  const { user } = useSelector((state: RootState) => state.auth);

  const [activeTab, setActiveTab] = useState("profile");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isUpdatingMobile, setIsUpdatingMobile] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Password update form
  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Mobile number form
  const mobileForm = useForm<MobileFormValues>({
    resolver: zodResolver(mobileFormSchema),
    defaultValues: {
      mobileNumber: user?.mobile || "",
    },
  });

  // Delete account form
  const deleteAccountForm = useForm<{ confirmText: string }>({
    resolver: zodResolver(deleteAccountSchema),
    defaultValues: {
      confirmText: "",
    },
  });

  // Update mobile form when user data changes
  useEffect(() => {
    if (user?.mobile) {
      mobileForm.setValue("mobileNumber", user.mobile);
    }
  }, [user, mobileForm]);

  // Handle password update
  const onPasswordSubmit = async (data: PasswordFormValues) => {
    // Double-check passwords match before submitting
    if (data.newPassword !== data.confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure your new password and confirmation match.",
        variant: "destructive",
      });
      return;
    }

    setIsUpdatingPassword(true);
    try {
      // This would be an actual API call in a real implementation
      await new Promise((resolve) => setTimeout(resolve, 1500));

      toast({
        title: "Password updated",
        description: "Your password has been updated successfully.",
        variant: "default",
      });

      passwordForm.reset({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      toast({
        title: "Failed to update password",
        description: "There was an error updating your password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  // Handle mobile number update
  const onMobileSubmit = async (data: MobileFormValues) => {
    setIsUpdatingMobile(true);
    try {
      // This would be an actual API call in a real implementation
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // In a real implementation, we would update the user's mobile number
      console.log('Updating mobile number to:', data.mobileNumber);

      toast({
        title: "Mobile number updated",
        description: "Your mobile number has been updated successfully.",
        variant: "default",
      });
    } catch (error) {
      toast({
        title: "Failed to update mobile number",
        description: "There was an error updating your mobile number. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingMobile(false);
    }
  };

  // Handle account deletion
  const onDeleteAccount = async (data: { confirmText: string }) => {
    setIsDeleting(true);
    try {
      // Verify the confirmation text one more time
      if (data.confirmText !== "DELETE") {
        toast({
          title: "Confirmation failed",
          description: 'Please type "DELETE" to confirm account deletion.',
          variant: "destructive",
        });
        setIsDeleting(false);
        return;
      }

      // This would be an actual API call in a real implementation
      await new Promise((resolve) => setTimeout(resolve, 2000));

      console.log('Deleting account with confirmation:', data.confirmText);

      toast({
        title: "Account deleted",
        description: "Your account has been deleted successfully. You will be redirected to the home page.",
        variant: "default",
      });

      // In a real implementation, you would dispatch a logout action here
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (error) {
      toast({
        title: "Failed to delete account",
        description: "There was an error deleting your account. Please try again.",
        variant: "destructive",
      });
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto py-8">
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle>Not Authenticated</CardTitle>
            <CardDescription>You need to be logged in to access settings.</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={() => navigate("/signin")}>Sign In</Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 py-8">
      <div className="container mx-auto px-4">
        {/* Back button */}
        <Button
          variant="ghost"
          size="sm"
          className="mb-6 group"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" />
          Back
        </Button>

        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h1 className="text-3xl font-bold mb-2">Account Settings</h1>
            <p className="text-muted-foreground mb-6">
              Manage your account settings and preferences
            </p>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid grid-cols-3 mb-8">
                <TabsTrigger value="profile" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Profile
                </TabsTrigger>
                <TabsTrigger value="security" className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Security
                </TabsTrigger>
                <TabsTrigger value="danger" className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Danger Zone
                </TabsTrigger>
              </TabsList>

              {/* Profile Tab */}
              <TabsContent value="profile" className="space-y-6">
                <Card className="overflow-hidden border-none shadow-md">
                  <div className="bg-gradient-to-r from-blue-600/10 to-indigo-600/5 px-6 py-4 border-b">
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <div className="bg-blue-100 p-2 rounded-full">
                        <Smartphone className="h-5 w-5 text-blue-600" />
                      </div>
                      Mobile Number
                    </CardTitle>
                    <CardDescription className="mt-1 ml-9">
                      Add or update your mobile number for account recovery and notifications
                    </CardDescription>
                  </div>
                  <CardContent className="p-6">
                    <div className="bg-blue-50/50 rounded-lg p-4 mb-6 border border-blue-100 flex items-start gap-3">
                      <div className="bg-blue-100 p-1.5 rounded-full mt-0.5">
                        <Info className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <p className="font-medium text-blue-700 mb-1">Why add a mobile number?</p>
                        <ul className="list-disc list-inside space-y-1 text-sm">
                          <li>Secure your account with two-factor authentication</li>
                          <li>Receive important notifications about your account</li>
                          <li>Recover access if you forget your password</li>
                        </ul>
                      </div>
                    </div>

                    <Form {...mobileForm}>
                      <form onSubmit={mobileForm.handleSubmit(onMobileSubmit)} className="space-y-6">
                        <FormField
                          control={mobileForm.control}
                          name="mobileNumber"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-base font-medium">Mobile Number</FormLabel>
                              <div className="relative mt-2">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                  <Phone className="h-5 w-5 text-muted-foreground" />
                                </div>
                                <FormControl>
                                  <Input
                                    placeholder="+1 (555) 123-4567"
                                    {...field}
                                    className="pl-10 py-6 text-base bg-muted/30 border-muted focus-visible:ring-blue-500 focus-visible:border-blue-500"
                                  />
                                </FormControl>
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                  {field.value ? (
                                    field.value.match(/^\+?[0-9\s\(\)\-]+$/) ? (
                                      <CheckCircle className="h-5 w-5 text-green-500" />
                                    ) : (
                                      <AlertCircle className="h-5 w-5 text-red-500" />
                                    )
                                  ) : null}
                                </div>
                              </div>
                              <FormDescription className="flex items-center gap-2 mt-2 text-sm">
                                <Globe className="h-4 w-4 text-muted-foreground" />
                                Include your country code (e.g., +1 for US, +44 for UK)
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <div className="flex flex-col sm:flex-row gap-3 pt-2">
                          <Button
                            type="submit"
                            className="flex-1 sm:flex-none py-5 px-6 bg-blue-600 hover:bg-blue-700 text-white"
                            disabled={isUpdatingMobile}
                          >
                            {isUpdatingMobile ? (
                              <>
                                <svg className="mr-2 h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Updating...
                              </>
                            ) : (
                              <>
                                <Save className="mr-2 h-4 w-4" />
                                Save Mobile Number
                              </>
                            )}
                          </Button>
                          {user?.mobile && (
                            <Button
                              type="button"
                              variant="outline"
                              className="flex-1 sm:flex-none py-5"
                              onClick={() => {
                                mobileForm.reset({ mobileNumber: "" });
                                toast({
                                  title: "Mobile number removed",
                                  description: "Your mobile number has been removed from your account.",
                                  variant: "default",
                                });
                              }}
                            >
                              <XCircle className="mr-2 h-4 w-4" />
                              Remove Number
                            </Button>
                          )}
                        </div>

                        {user?.mobile && (
                          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-100 rounded-md text-sm text-green-700">
                            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                            <span>
                              Your current mobile number is <span className="font-medium">{user.mobile}</span>
                            </span>
                          </div>
                        )}
                      </form>
                    </Form>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Security Tab */}
              <TabsContent value="security" className="space-y-6">
                <Card className="overflow-hidden border-none shadow-md">
                  <div className="bg-gradient-to-r from-indigo-600/10 to-violet-600/5 px-6 py-4 border-b">
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <div className="bg-indigo-100 p-2 rounded-full">
                        <Key className="h-5 w-5 text-indigo-600" />
                      </div>
                      Change Password
                    </CardTitle>
                    <CardDescription className="mt-1 ml-9">
                      Update your password to keep your account secure
                    </CardDescription>
                  </div>
                  <CardContent className="p-6">
                    <div className="bg-indigo-50/50 rounded-lg p-4 mb-6 border border-indigo-100 flex items-start gap-3">
                      <div className="bg-indigo-100 p-1.5 rounded-full mt-0.5">
                        <Info className="h-4 w-4 text-indigo-600" />
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <p className="font-medium text-indigo-700 mb-1">Password security tips</p>
                        <ul className="list-disc list-inside space-y-1 text-sm">
                          <li>Use a combination of letters, numbers, and special characters</li>
                          <li>Avoid using personal information in your password</li>
                          <li>Use a different password for each of your accounts</li>
                        </ul>
                      </div>
                    </div>

                    <Form {...passwordForm}>
                      <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-6">
                        <FormField
                          control={passwordForm.control}
                          name="currentPassword"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-base font-medium">Current Password</FormLabel>
                              <div className="relative mt-2">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                  <Lock className="h-5 w-5 text-muted-foreground" />
                                </div>
                                <FormControl>
                                  <Input
                                    type={showCurrentPassword ? "text" : "password"}
                                    placeholder="Enter your current password"
                                    {...field}
                                    className="pl-10 py-6 text-base bg-muted/30 border-muted focus-visible:ring-indigo-500 focus-visible:border-indigo-500"
                                  />
                                </FormControl>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                >
                                  {showCurrentPassword ? (
                                    <EyeOff className="h-5 w-5 text-muted-foreground" />
                                  ) : (
                                    <Eye className="h-5 w-5 text-muted-foreground" />
                                  )}
                                </Button>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={passwordForm.control}
                          name="newPassword"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-base font-medium">New Password</FormLabel>
                              <div className="relative mt-2">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                  <Key className="h-5 w-5 text-muted-foreground" />
                                </div>
                                <FormControl>
                                  <Input
                                    type={showNewPassword ? "text" : "password"}
                                    placeholder="Enter your new password"
                                    {...field}
                                    className="pl-10 py-6 text-base bg-muted/30 border-muted focus-visible:ring-indigo-500 focus-visible:border-indigo-500"
                                  />
                                </FormControl>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                  onClick={() => setShowNewPassword(!showNewPassword)}
                                >
                                  {showNewPassword ? (
                                    <EyeOff className="h-5 w-5 text-muted-foreground" />
                                  ) : (
                                    <Eye className="h-5 w-5 text-muted-foreground" />
                                  )}
                                </Button>
                                {field.value && (
                                  <div className="absolute right-12 top-1/2 -translate-y-1/2">
                                    {field.value.length >= 8 &&
                                     /[A-Z]/.test(field.value) &&
                                     /[a-z]/.test(field.value) &&
                                     /[0-9]/.test(field.value) &&
                                     /[^A-Za-z0-9]/.test(field.value) ? (
                                      <CheckCircle className="h-5 w-5 text-green-500" />
                                    ) : (
                                      <AlertCircle className="h-5 w-5 text-amber-500" />
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="mt-3 space-y-2">
                                <p className="text-sm font-medium text-indigo-700">Password requirements:</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  <div className={`flex items-center gap-2 text-xs ${field.value && field.value.length >= 8 ? 'text-green-600' : 'text-muted-foreground'}`}>
                                    {field.value && field.value.length >= 8 ? (
                                      <CheckCircle className="h-4 w-4 text-green-500" />
                                    ) : (
                                      <Circle className="h-4 w-4" />
                                    )}
                                    At least 8 characters
                                  </div>
                                  <div className={`flex items-center gap-2 text-xs ${field.value && /[A-Z]/.test(field.value) ? 'text-green-600' : 'text-muted-foreground'}`}>
                                    {field.value && /[A-Z]/.test(field.value) ? (
                                      <CheckCircle className="h-4 w-4 text-green-500" />
                                    ) : (
                                      <Circle className="h-4 w-4" />
                                    )}
                                    One uppercase letter
                                  </div>
                                  <div className={`flex items-center gap-2 text-xs ${field.value && /[a-z]/.test(field.value) ? 'text-green-600' : 'text-muted-foreground'}`}>
                                    {field.value && /[a-z]/.test(field.value) ? (
                                      <CheckCircle className="h-4 w-4 text-green-500" />
                                    ) : (
                                      <Circle className="h-4 w-4" />
                                    )}
                                    One lowercase letter
                                  </div>
                                  <div className={`flex items-center gap-2 text-xs ${field.value && /[0-9]/.test(field.value) ? 'text-green-600' : 'text-muted-foreground'}`}>
                                    {field.value && /[0-9]/.test(field.value) ? (
                                      <CheckCircle className="h-4 w-4 text-green-500" />
                                    ) : (
                                      <Circle className="h-4 w-4" />
                                    )}
                                    One number
                                  </div>
                                  <div className={`flex items-center gap-2 text-xs ${field.value && /[^A-Za-z0-9]/.test(field.value) ? 'text-green-600' : 'text-muted-foreground'}`}>
                                    {field.value && /[^A-Za-z0-9]/.test(field.value) ? (
                                      <CheckCircle className="h-4 w-4 text-green-500" />
                                    ) : (
                                      <Circle className="h-4 w-4" />
                                    )}
                                    One special character
                                  </div>
                                </div>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={passwordForm.control}
                          name="confirmPassword"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-base font-medium">Confirm New Password</FormLabel>
                              <div className="relative mt-2">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                  <Lock className="h-5 w-5 text-muted-foreground" />
                                </div>
                                <FormControl>
                                  <Input
                                    type={showConfirmPassword ? "text" : "password"}
                                    placeholder="Confirm your new password"
                                    {...field}
                                    className="pl-10 py-6 text-base bg-muted/30 border-muted focus-visible:ring-indigo-500 focus-visible:border-indigo-500"
                                  />
                                </FormControl>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                  {showConfirmPassword ? (
                                    <EyeOff className="h-5 w-5 text-muted-foreground" />
                                  ) : (
                                    <Eye className="h-5 w-5 text-muted-foreground" />
                                  )}
                                </Button>
                                {field.value && passwordForm.getValues().newPassword && (
                                  <div className="absolute right-12 top-1/2 -translate-y-1/2">
                                    {field.value === passwordForm.getValues().newPassword ? (
                                      <CheckCircle className="h-5 w-5 text-green-500" />
                                    ) : (
                                      <AlertCircle className="h-5 w-5 text-red-500" />
                                    )}
                                  </div>
                                )}
                              </div>
                              {field.value && passwordForm.getValues().newPassword && field.value !== passwordForm.getValues().newPassword && (
                                <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
                                  <AlertCircle className="h-4 w-4" />
                                  Passwords don't match
                                </p>
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <div className="pt-4">
                          <Button
                            type="submit"
                            className="w-full sm:w-auto py-5 px-6 bg-indigo-600 hover:bg-indigo-700 text-white"
                            disabled={isUpdatingPassword || !passwordForm.formState.isValid}
                          >
                            {isUpdatingPassword ? (
                              <>
                                <svg className="mr-2 h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Updating Password...
                              </>
                            ) : (
                              <>
                                <Lock className="mr-2 h-4 w-4" />
                                Update Password
                              </>
                            )}
                          </Button>
                        </div>
                      </form>
                    </Form>
                  </CardContent>
                </Card>

                <Card className="overflow-hidden border-none shadow-md">
                  <div className="bg-gradient-to-r from-green-600/10 to-teal-600/5 px-6 py-4 border-b">
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <div className="bg-green-100 p-2 rounded-full">
                        <LogOut className="h-5 w-5 text-green-600" />
                      </div>
                      Active Sessions
                    </CardTitle>
                    <CardDescription className="mt-1 ml-9">
                      Manage your active sessions and sign out from other devices
                    </CardDescription>
                  </div>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="bg-green-100 p-2 rounded-full">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium">Current Session</p>
                            <p className="text-sm text-muted-foreground">
                              {navigator.userAgent.includes("Windows") ? "Windows" :
                               navigator.userAgent.includes("Mac") ? "MacOS" :
                               navigator.userAgent.includes("Linux") ? "Linux" : "Unknown"} •
                              {navigator.userAgent.includes("Chrome") ? " Chrome" :
                               navigator.userAgent.includes("Firefox") ? " Firefox" :
                               navigator.userAgent.includes("Safari") ? " Safari" : " Unknown Browser"}
                            </p>
                          </div>
                        </div>
                        <Button variant="outline" size="sm" disabled>
                          Current
                        </Button>
                      </div>

                      {/* Example of another session */}
                      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-100 p-2 rounded-full">
                            <Smartphone className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">Mobile App</p>
                            <p className="text-sm text-muted-foreground">
                              iOS • Last active 2 hours ago
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            toast({
                              title: "Signed out",
                              description: "You have been signed out from your mobile device.",
                              variant: "default",
                            });
                          }}
                        >
                          Sign Out
                        </Button>
                      </div>
                    </div>

                    <div className="mt-6">
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          toast({
                            title: "Signed out from all devices",
                            description: "You have been signed out from all devices.",
                            variant: "default",
                          });
                        }}
                      >
                        Sign Out From All Devices
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Danger Zone Tab */}
              <TabsContent value="danger" className="space-y-6">
                <Card className="border-none shadow-md overflow-hidden">
                  <div className="bg-gradient-to-r from-red-600/10 to-red-500/5 px-6 py-4 border-b border-red-200/30">
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <div className="bg-red-100 p-2 rounded-full">
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                      </div>
                      Danger Zone
                    </CardTitle>
                    <CardDescription className="mt-1 ml-9 text-red-700/70">
                      These actions are irreversible. Please proceed with caution.
                    </CardDescription>
                  </div>
                  <CardContent className="pt-6">
                    <div className="space-y-6">
                      <div className="p-4 border border-destructive/30 rounded-lg">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div>
                            <h3 className="font-semibold text-destructive flex items-center gap-2">
                              <Trash2 className="h-5 w-5" />
                              Delete Account
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              Permanently delete your account and all associated data. This action cannot be undone.
                            </p>
                          </div>
                          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                            <AlertDialogTrigger asChild>
                              <Button variant="destructive" size="sm">
                                Delete Account
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-destructive flex items-center gap-2">
                                  <AlertTriangle className="h-5 w-5" />
                                  Delete Account
                                </AlertDialogTitle>
                                <AlertDialogDescription className="space-y-4">
                                  <p>
                                    This action <span className="font-bold">cannot be undone</span>.
                                    This will permanently delete your account and remove all your data from our servers.
                                  </p>
                                  <div className="p-3 bg-destructive/10 rounded-md border border-destructive/20 text-sm">
                                    <p className="font-medium text-destructive mb-1">You will lose:</p>
                                    <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                                      <li>All your profile information</li>
                                      <li>Access to your enrolled courses</li>
                                      <li>Your learning progress and achievements</li>
                                      <li>Any content you've created</li>
                                    </ul>
                                  </div>
                                  <Form {...deleteAccountForm}>
                                    <form id="delete-account-form" onSubmit={deleteAccountForm.handleSubmit(onDeleteAccount)}>
                                      <FormField
                                        control={deleteAccountForm.control}
                                        name="confirmText"
                                        render={({ field }) => (
                                          <FormItem>
                                            <FormLabel className="text-destructive">
                                              Type "DELETE" to confirm
                                            </FormLabel>
                                            <FormControl>
                                              <Input
                                                placeholder="DELETE"
                                                className="border-destructive/50 focus-visible:ring-destructive"
                                                {...field}
                                              />
                                            </FormControl>
                                            <FormMessage />
                                          </FormItem>
                                        )}
                                      />
                                    </form>
                                  </Form>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <Button
                                  variant="destructive"
                                  type="submit"
                                  form="delete-account-form"
                                  disabled={isDeleting || !deleteAccountForm.formState.isValid}
                                >
                                  {isDeleting ? (
                                    <>
                                      <svg className="mr-2 h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                      Deleting...
                                    </>
                                  ) : (
                                    "Delete Account"
                                  )}
                                </Button>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>

                      <div className="p-4 border border-orange-300/50 rounded-lg">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div>
                            <h3 className="font-semibold text-orange-600 flex items-center gap-2">
                              <XCircle className="h-5 w-5" />
                              Deactivate Account
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              Temporarily deactivate your account. You can reactivate it later by signing in.
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            className="border-orange-300 text-orange-600 hover:bg-orange-50"
                            onClick={() => {
                              toast({
                                title: "Account deactivated",
                                description: "Your account has been temporarily deactivated. You can reactivate it by signing in again.",
                                variant: "default",
                              });
                              setTimeout(() => {
                                navigate("/");
                              }, 2000);
                            }}
                          >
                            Deactivate
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
